import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useCallback } from "react";
import { useSelector } from "react-redux";
import { PublicAudioCodingFilterCard } from "~/components/Filters/AudioCodingFilter/PublicAudioCodingFilterCard";
import { PublicAudioSampleRateFilterCard } from "~/components/Filters/AudioSampleRateFilter/PublicAudioSampleRateFilterCard";
import { PublicCamerasFilterCard } from "~/components/Filters/CamerasFilter/PublicCamerasFilterCard";
import { PublicCityFilterCard } from "~/components/Filters/CityFilter/PublicCityFilterCard";
import { ColorFilterCard } from "~/components/Filters/ColorFilter/ColorFilterCard";
import { PublicCopyrightFilterCard } from "~/components/Filters/CopyrightFilter/PublicCopyrightFilterCard";
import { PublicCountryFilterCard } from "~/components/Filters/CountryFilter/PublicCountryFilterCard";
import { PublicCreatorFilterCard } from "~/components/Filters/CreatorFilter/PublicCreatorFilterCard";
import { PublicCustomFieldFilterCard } from "~/components/Filters/CustomFieldFilter/PublicCustomFieldFilterCard";
import { PublicExtensionsFilterCard } from "~/components/Filters/ExtensionFilter/PublicExtensionsFilterCard";
import { FiltersDropdown } from "~/components/Filters/FiltersDropdown/FiltersDropdown";
import { OrientationFilterCard } from "~/components/Filters/OrientationFilter/OrientationFilterCard";
import { PublicSourceFilterCard } from "~/components/Filters/SourceFilter/PublicSourceFilterCard";
import { PublicStateFilterCard } from "~/components/Filters/StateFilter/PublicStateFilterCard";
import { PublicVideoAspectRatioFilterCard } from "~/components/Filters/VideoAspectRatioFilter/PublicVideoAspectRatioFilterCard";
import { PublicVideoFrameRateFilterCard } from "~/components/Filters/VideoFrameRateFilter/PublicVideoFrameRateFilterCard";
import { useDidFilterChange } from "~/components/SavedFilter/shared/hooks/useDidFilterChange";
import { useAppliedCustomFieldsFilters } from "~/hooks/filters/useAppliedCustomFieldsFilters";
import { useAppliedTagsFilters } from "~/hooks/filters/useAppliedTagsFilters";
import { centralizedBoardIdSelector } from "~/store/centralizedBoard/selectors";
import { useGetPublicFilterOptions } from "~/swr-hooks/filters/useGetPublicFilterOptions";
import { useGetPublicFiltersEntities } from "~/swr-hooks/filters/useGetPublicFiltersEntities";
export const PublicFiltersDropdown = /*#__PURE__*/ memo((props)=>{
    const boardId = useSelector(centralizedBoardIdSelector);
    const { data: filterOptions } = useGetPublicFilterOptions({
        boardId
    });
    const { appliedTagsFilters } = useAppliedTagsFilters();
    const { appliedCustomFieldsFilters } = useAppliedCustomFieldsFilters();
    const { data: entities } = useGetPublicFiltersEntities({
        tagLabels: appliedTagsFilters,
        customFields: appliedCustomFieldsFilters
    });
    const { isFilterChanged } = useDidFilterChange({
        options: filterOptions,
        entities
    });
    const renderColorFilterCard = useCallback((props)=>/*#__PURE__*/ {
        var _filterOptions_colors;
        return _jsx(ColorFilterCard, {
            colors: (_filterOptions_colors = filterOptions === null || filterOptions === void 0 ? void 0 : filterOptions.colors) !== null && _filterOptions_colors !== void 0 ? _filterOptions_colors : [],
            ...props
        });
    }, [
        filterOptions === null || filterOptions === void 0 ? void 0 : filterOptions.colors
    ]);
    const renderSourceFilterCard = useCallback((props)=>/*#__PURE__*/ _jsx(PublicSourceFilterCard, {
            boardId: boardId,
            ...props
        }), [
        boardId
    ]);
    const renderExtensionFilterCard = useCallback((props)=>/*#__PURE__*/ _jsx(PublicExtensionsFilterCard, {
            boardId: boardId,
            ...props
        }), [
        boardId
    ]);
    const renderCustomFieldCard = useCallback((props)=>/*#__PURE__*/ _jsx(PublicCustomFieldFilterCard, {
            ...props
        }), []);
    const renderCopyrightFilterCard = useCallback((props)=>/*#__PURE__*/ _jsx(PublicCopyrightFilterCard, {
            boardId: boardId,
            ...props
        }), [
        boardId
    ]);
    const renderCreatorFilterCard = useCallback((props)=>/*#__PURE__*/ _jsx(PublicCreatorFilterCard, {
            boardId: boardId,
            ...props
        }), [
        boardId
    ]);
    const renderCamerasFilterCard = useCallback((props)=>/*#__PURE__*/ _jsx(PublicCamerasFilterCard, {
            boardId: boardId,
            ...props
        }), [
        boardId
    ]);
    const renderVideoAspectRatioFilterCard = useCallback((props)=>/*#__PURE__*/ _jsx(PublicVideoAspectRatioFilterCard, {
            boardId: boardId,
            ...props
        }), [
        boardId
    ]);
    const renderCountryFilterCard = useCallback((props)=>/*#__PURE__*/ _jsx(PublicCountryFilterCard, {
            boardId: boardId,
            ...props
        }), [
        boardId
    ]);
    const renderCityFilterCard = useCallback((props)=>/*#__PURE__*/ _jsx(PublicCityFilterCard, {
            boardId: boardId,
            ...props
        }), [
        boardId
    ]);
    const renderStateFilterCard = useCallback((props)=>/*#__PURE__*/ _jsx(PublicStateFilterCard, {
            boardId: boardId,
            ...props
        }), [
        boardId
    ]);
    const renderVideoFrameRateFilterCard = useCallback((props)=>/*#__PURE__*/ _jsx(PublicVideoFrameRateFilterCard, {
            boardId: boardId,
            ...props
        }), [
        boardId
    ]);
    const renderAudioCodingFilterCard = useCallback((props)=>/*#__PURE__*/ _jsx(PublicAudioCodingFilterCard, {
            boardId: boardId,
            ...props
        }), [
        boardId
    ]);
    const renderAudioSampleRateFilterCard = useCallback((props)=>/*#__PURE__*/ _jsx(PublicAudioSampleRateFilterCard, {
            boardId: boardId,
            ...props
        }), [
        boardId
    ]);
    const renderOrientationFilterCard = useCallback((props)=>/*#__PURE__*/ _jsx(OrientationFilterCard, {
            ...props
        }), []);
    return /*#__PURE__*/ _jsx(FiltersDropdown, {
        OrientationFilterCard: renderOrientationFilterCard,
        VideoFrameRateFilterCard: renderVideoFrameRateFilterCard,
        VideoAspectRatioFilterCard: renderVideoAspectRatioFilterCard,
        CreatorFilterCard: renderCreatorFilterCard,
        CountryFilterCard: renderCountryFilterCard,
        CityFilterCard: renderCityFilterCard,
        StateFilterCard: renderStateFilterCard,
        AudioCodingFilterCard: renderAudioCodingFilterCard,
        AudioSampleRateFilterCard: renderAudioSampleRateFilterCard,
        CameraFilterCard: renderCamerasFilterCard,
        ColorFilterCard: renderColorFilterCard,
        CopyrightFilterCard: renderCopyrightFilterCard,
        SourceFilterCard: renderSourceFilterCard,
        CustomFieldFilterCard: renderCustomFieldCard,
        ExtensionFilterCard: renderExtensionFilterCard,
        isFilterChanged: isFilterChanged,
        ...props
    });
});
PublicFiltersDropdown.displayName = "PublicFiltersDropdown";
